// src/components/tactic/DraftImportPanel.jsx
// Pull a real draft out of the uploaded workbook and place all ten heroes on
// their lanes, so analysis starts from what actually happened in the game.

import React, { useMemo, useRef, useState } from "react";
import { getHeroImageUrlWithFallback } from "../../utils/heroCatalog";
import { bansFromGame, describeGame, sideFromGame } from "../../utils/tactic/draftImport";
import { Btn, Label, Panel, Row, T, TextInput } from "./tacticUi";

/** Screenshot → draft, for the games that never made it into the workbook. */
function ImagePicker({ busy, error, onPickImage }) {
  const fileRef = useRef(null);
  return (
    <div style={{ marginBottom: 10 }}>
      <Btn
        tone="accent"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        style={{ width: "100%" }}
      >
        {busy ? "กำลังอ่านรูป…" : "📷 วางดราฟจากสกรีนช็อต"}
      </Btn>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPickImage(f);
          e.target.value = "";
        }}
      />
      {error && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: "#ff8ba3", lineHeight: 1.6 }}>
          {error}
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 10.5, color: T.faint, lineHeight: 1.6 }}>
        ใช้รูปตารางดราฟ — AI จะอ่านชื่อฮีโร่แล้ววางลงเลนให้ ตรวจก่อนใช้เสมอ
      </div>
    </div>
  );
}

export default function DraftImportPanel({
  games,
  onImport,
  onImportImage,
  imageBusy,
  imageError,
}) {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = (games || []).map((g, i) => ({ g, i, label: describeGame(g, i) }));
    if (!q) return rows.slice(0, 60);
    return rows.filter((r) => r.label.toLowerCase().includes(q)).slice(0, 60);
  }, [games, query]);

  if (!games?.length) {
    return (
      <Panel title="โหลดดราฟ">
        <ImagePicker busy={imageBusy} error={imageError} onPickImage={onImportImage} />
        <div style={{ color: T.faint, fontSize: 12.5, lineHeight: 1.7 }}>
          ยังไม่มีข้อมูลเกมจากไฟล์ — อัปโหลด .xlsx ที่หน้าแรก แล้วดราฟทุกเกมจะมาโผล่ที่นี่
          ให้เลือกวางลงแมพได้ทันที
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title="โหลดดราฟ"
      right={<span style={{ color: T.faint, fontSize: 11 }}>{games.length} เกม</span>}
    >
      <ImagePicker busy={imageBusy} error={imageError} onPickImage={onImportImage} />
      <TextInput
        placeholder="ค้นหาวันที่ / คู่แข่ง…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      <div style={{ maxHeight: 260, overflowY: "auto", display: "grid", gap: 5 }}>
        {list.map(({ g, i, label }) => {
          const id = `${i}`;
          const open = openId === id;
          const bans = open ? bansFromGame(g) : null;
          const picks = [
            ...(g.fsPick || []).slice(0, 5),
          ];
          return (
            <div
              key={id}
              style={{
                borderRadius: 8,
                background: open ? "rgba(255,16,38,0.09)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${open ? "rgba(255,16,38,0.38)" : T.line}`,
                overflow: "hidden",
              }}
            >
              <button
                type="button"
                onClick={() => setOpenId(open ? null : id)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "7px 9px",
                  color: T.ink,
                  fontFamily: T.font,
                  fontSize: 12.5,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: g.win ? T.green : g.lose ? T.red : T.faint,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {label}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 3, marginTop: 5 }}>
                  {picks.filter(Boolean).map((h, k) => (
                    <img
                      key={`${h}-${k}`}
                      src={getHeroImageUrlWithFallback(h)}
                      alt={h}
                      title={h}
                      style={{ width: 22, height: 22, borderRadius: 4, objectFit: "cover" }}
                    />
                  ))}
                </div>
              </button>

              {open && (
                <div style={{ padding: "0 9px 9px" }}>
                  {bans && (bans.ours.length > 0 || bans.theirs.length > 0) && (
                    <div style={{ fontSize: 10.5, color: T.faint, marginBottom: 7, lineHeight: 1.6 }}>
                      {bans.ours.length > 0 && <div>แบนเรา: {bans.ours.join(", ")}</div>}
                      {bans.theirs.length > 0 && <div>แบนคู่แข่ง: {bans.theirs.join(", ")}</div>}
                    </div>
                  )}
                  <Label>วางลงแมพโดยให้เราอยู่ฝั่ง</Label>
                  <Row gap={6}>
                    <Btn tone="blue" onClick={() => onImport(g, "blue")} style={{ flex: 1 }}>
                      บลู
                    </Btn>
                    <Btn tone="red" onClick={() => onImport(g, "red")} style={{ flex: 1 }}>
                      เรด
                    </Btn>
                    <Btn tone="accent" onClick={() => onImport(g, sideFromGame(g))} title="ใช้ฝั่งตามที่บันทึกในไฟล์">
                      ตามไฟล์
                    </Btn>
                  </Row>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
